import { useCallback } from 'react'
import { RecentSearch } from '../../types/search'
import {
  MAX_RECENT_SEARCHES,
  RECENT_SEARCHES_KEY,
} from '../../const/recent-searches'
import { logger } from 'helpers/logger'
import { useRemoteLocalStorage } from 'hooks/useRemoteLocalStorage'

export const useRecentSearches = () => {
  const {
    value: recentSearches,
    setValue,
    remove,
  } = useRemoteLocalStorage<RecentSearch[]>({
    scope: 'user',
    key: RECENT_SEARCHES_KEY,
    defaultValue: [],
  })

  const addRecentSearch = useCallback(
    (searches: Omit<RecentSearch, 'id'>[]) => {
      if (!searches.some((s) => s.value.trim())) return

      const newSearches: RecentSearch[] = searches.map((search) => ({
        ...search,
        id: `${search.type}-${search.value}-${Date.now()}-${Math.random()}`,
        title: search.title || '',
        avatarSrc: search.avatarSrc || '',
        searchValue: search.searchValue,
      }))

      setValue((prev) => {
        const safePrev = Array.isArray(prev) ? prev : []
        return [
          ...newSearches,
          ...safePrev.filter(
            (existing) =>
              !searches.some(
                (s) => s.value === existing.value && s.type === existing.type,
              ),
          ),
        ].slice(0, MAX_RECENT_SEARCHES)
      })
    },
    [setValue],
  )

  const clearRecentSearches = useCallback(() => {
    try {
      remove()
    } catch {
      logger.error('Error clearing recent searches from localStorage')
    }
  }, [remove])

  return {
    recentSearches,
    addRecentSearch,
    clearRecentSearches,
  }
}
